// Aggregated telemetry for the query permitting.
//
// Each metric accumulates in memory via its own record* methods and
// serializes to zero or more track events on drain,resetting its counters.

import {QUERY_PERMIT_SOFT_THROTTLE_EVENT, QueryPermitClientType, QueryPermitTrackEvent} from './tracking';

const MS_PER_MINUTE = 60_000;

// Backoff levels at or above this collapse into the top bucket
const SOFT_THROTTLE_TOP_LEVEL = 5;

export interface QueryPermitMetric {
  // Serialize accumulated state into zero or more track events and reset.
  drain(nowMs: number): QueryPermitTrackEvent[];
}

interface SoftThrottleAccumulator {
  // nowMs when the window's first sample landed — the denominator anchor for sustained_rpm
  startTime: number;
  // Total number of queries
  sampleCount: number;
  // Number of queries that were throttled
  engagedCount: number;
  // bucketLevel -> count of queries at that backoff depth
  levelCounts: Map<number, number>;
}

export class SoftThrottleMetric implements QueryPermitMetric {
  private readonly perProject = new Map<number, SoftThrottleAccumulator>();

  constructor(private readonly clientType: QueryPermitClientType) {}

  // Record one soft-throttle-evaluated grant
  record(projectId: number, backoffLevel: number, nowMs: number): void {
    let acc = this.perProject.get(projectId);
    if (!acc) {
      acc = {startTime: nowMs, sampleCount: 0, engagedCount: 0, levelCounts: new Map()};
      this.perProject.set(projectId, acc);
    }
    acc.sampleCount++;
    if (backoffLevel > 0) {
      acc.engagedCount++;
      const bucket = Math.min(backoffLevel, SOFT_THROTTLE_TOP_LEVEL);
      acc.levelCounts.set(bucket, (acc.levelCounts.get(bucket) ?? 0) + 1);
    }
  }

  drain(nowMs: number): QueryPermitTrackEvent[] {
    const events: QueryPermitTrackEvent[] = [];
    for (const [projectId, acc] of this.perProject) {
      // Only windows that engaged the throttle are worth reporting.
      if (acc.engagedCount === 0) {
        continue;
      }
      const elapsedMs = nowMs - acc.startTime;
      events.push({
        name: QUERY_PERMIT_SOFT_THROTTLE_EVENT,
        properties: {
          project_id: projectId,
          client_type: this.clientType,
          engaged_count: acc.engagedCount,
          backoff_level_1: acc.levelCounts.get(1) ?? 0,
          backoff_level_2: acc.levelCounts.get(2) ?? 0,
          backoff_level_3: acc.levelCounts.get(3) ?? 0,
          backoff_level_4: acc.levelCounts.get(4) ?? 0,
          backoff_level_5_plus: acc.levelCounts.get(SOFT_THROTTLE_TOP_LEVEL) ?? 0,
          sustained_rpm: elapsedMs > 0 ? (acc.sampleCount / elapsedMs) * MS_PER_MINUTE : 0,
        },
      });
    }
    this.perProject.clear();
    return events;
  }
}
